<template>
  <SaleSharedSummaryTab
    v-if="activeTab === 'summary'"
    :sale="sale"
    @edit-section="$emit('edit-section', $event)"
    @register-plan-payment="$emit('new-payment-plan')"
    @new-expense="$emit('new-expense-from-sale')"
    @new-fee="$emit('new-fee-from-sale')"
    @view-history="$emit('open-history-tab')"
  />

  <SaleSharedDetailTab
    v-else-if="activeTab === 'details'"
    :sale="sale"
    @edit-section="$emit('edit-section', $event)"
  />

  <SalePaymentPlanTab
    v-else-if="activeTab === 'payment-plan'"
    :sale="sale"
    :show-customer-filter="isMultiCustomerSale"
    :initial-customer-id="paymentPlanInitialCustomerId"
    :open-modal-request-id="paymentPlanOpenModalRequestId"
    @new-payment-plan="$emit('new-payment-plan', $event)"
    @payment-plan-saved="$emit('payment-plan-saved', $event)"
    @invoice-plan="$emit('invoice-plan', $event)"
  />

  <SaleCustomersTab
    v-else-if="activeTab === 'customers' && isMultiCustomerSale"
    :sale="sale"
    @open-payment-plan-for-customer="$emit('open-payment-plan-for-customer', $event)"
  />

  <SaleSharedExpensesTab
    v-else-if="activeTab === 'expenses'"
    :sale-id="saleId"
    :rows="expenseRows"
    :loading="expenseLoading"
    :filter-options="expenseFilterOptions"
    :pagination="expensePagination"
    :status-tabs="expenseStatusTabs"
    :active-status-tab="expenseActiveStatusTab"
    :summary="expenseSummary"
    @clear-expenses="$emit('clear-expenses-from-sale')"
    @new-expense="$emit('new-expense-from-sale')"
    @status-tab-select="$emit('expenses-status-tab-select', $event)"
    @search-change="$emit('expenses-search-change', $event)"
    @filters-change="$emit('expenses-filters-change', $event)"
    @page-change="$emit('expenses-page-change', $event)"
    @pay-expense="$emit('pay-expense', $event)"
    @edit-expense="$emit('edit-expense', $event)"
    @delete-expense="$emit('delete-expense', $event)"
    @approve-expense="$emit('approve-expense', $event)"
    @download-xml="$emit('download-xml', $event)"
    @download-pdf="$emit('download-pdf', $event)"
    @download-receipt="$emit('download-receipt', $event)"
  />

  <SaleSharedFeesTab
    v-else-if="activeTab === 'commissions'"
    :sale-id="saleId"
    :rows="feeRows"
    :loading="feeLoading"
    :filter-options="feeFilterOptions"
    :pagination="feePagination"
    :status-tabs="feeStatusTabs"
    :active-status-tab="feeActiveStatusTab"
    :summary="feeSummary"
    @clear-commissions="$emit('clear-fees-from-sale')"
    @new-fee="$emit('new-fee-from-sale')"
    @status-tab-select="$emit('fees-status-tab-select', $event)"
    @search-change="$emit('fees-search-change', $event)"
    @filters-change="$emit('fees-filters-change', $event)"
    @page-change="$emit('fees-page-change', $event)"
    @pay-expense="$emit('pay-fee', $event)"
    @edit-expense="$emit('edit-fee', $event)"
    @delete-expense="$emit('delete-fee', $event)"
    @approve-expense="$emit('approve-fee', $event)"
    @download-xml="$emit('download-fee-xml', $event)"
    @download-pdf="$emit('download-fee-pdf', $event)"
    @download-receipt="$emit('download-fee-receipt', $event)"
  />

  <SaleSharedDocumentsTab
    v-else-if="activeTab === 'documents'"
    :sale="sale"
  />

  <SaleSharedHistoryTab
    v-else-if="activeTab === 'history'"
    :sale-id="saleId"
  />

  <section v-else class="sale-detail-tab card">
    <div class="card-body">
      <header class="sale-detail-tab__header">
        <h4>{{ text.detail.tabsPanelFallback.title }}</h4>
        <p>{{ text.detail.tabsPanelFallback.subtitle }}</p>
      </header>

      <div class="sale-detail-tab__content">
        <p>{{ text.detail.tabsPanelFallback.message }}</p>
      </div>
    </div>
  </section>
</template>

<script>
import SaleSharedDetailTab from '@/components/sales/shared/detail/SaleSharedDetailTab.vue';
import SaleSharedSummaryTab from '@/components/sales/shared/summary/SaleSharedSummaryTab.vue';
import SalePaymentPlanTab from '@/components/sales/shared/paymentPlan/SalePaymentPlanTab.vue';
import SaleCustomersTab from '@/components/salesMultiCustomer/customers/SaleCustomersTab.vue';
import SaleSharedExpensesTab from '@/components/sales/shared/expenses/SaleSharedExpensesTab.vue';
import SaleSharedFeesTab from '@/components/sales/shared/fees/SaleSharedFeesTab.vue';
import SaleSharedDocumentsTab from '@/components/sales/shared/documents/SaleSharedDocumentsTab.vue';
import SaleSharedHistoryTab from '@/components/sales/shared/history/SaleSharedHistoryTab.vue';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('sales');

export default {
  name: 'SaleDetailTabsPanel',
  components: {
    SaleSharedSummaryTab,
    SaleSharedDetailTab,
    SalePaymentPlanTab,
    SaleCustomersTab,
    SaleSharedExpensesTab,
    SaleSharedFeesTab,
    SaleSharedDocumentsTab,
    SaleSharedHistoryTab,
  },
  data() {
    return {
      text,
    };
  },
  props: {
    activeTab: {
      type: String,
      required: true,
    },
    sale: {
      type: Object,
      default: null,
    },
    paymentPlanInitialCustomerId: {
      type: Number,
      default: 0,
    },
    paymentPlanOpenModalRequestId: {
      type: Number,
      default: 0,
    },
    expenseRows: {
      type: Array,
      default: () => [],
    },
    expenseLoading: {
      type: Boolean,
      default: false,
    },
    expenseFilterOptions: {
      type: Object,
      default: () => ({}),
    },
    expensePagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 30,
        total_pages: 1,
        total_items: 0,
      }),
    },
    expenseStatusTabs: {
      type: Array,
      default: () => [],
    },
    expenseActiveStatusTab: {
      type: String,
      default: 'all',
    },
    expenseSummary: {
      type: Object,
      default: () => ({
        total: 0,
        paid: 0,
        pending: 0,
      }),
    },
    feeRows: {
      type: Array,
      default: () => [],
    },
    feeLoading: {
      type: Boolean,
      default: false,
    },
    feeFilterOptions: {
      type: Object,
      default: () => ({}),
    },
    feePagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 30,
        total_pages: 1,
        total_items: 0,
      }),
    },
    feeStatusTabs: {
      type: Array,
      default: () => [],
    },
    feeActiveStatusTab: {
      type: String,
      default: 'all',
    },
    feeSummary: {
      type: Object,
      default: () => ({
        total: 0,
        paid: 0,
        pending: 0,
      }),
    },
  },
  emits: [
    'edit-section',
    'expenses-search-change',
    'expenses-filters-change',
    'expenses-status-tab-select',
    'expenses-page-change',
    'clear-expenses-from-sale',
    'new-expense-from-sale',
    'pay-expense',
    'edit-expense',
    'delete-expense',
    'approve-expense',
    'download-xml',
    'download-pdf',
    'download-receipt',
    'fees-search-change',
    'fees-filters-change',
    'fees-page-change',
    'pay-fee',
    'edit-fee',
    'delete-fee',
    'approve-fee',
    'download-fee-xml',
    'download-fee-pdf',
    'download-fee-receipt',
    'fees-status-tab-select',
    'clear-fees-from-sale',
    'new-fee-from-sale',
    'new-payment-plan',
    'payment-plan-saved',
    'invoice-plan',
    'open-payment-plan-for-customer',
    'open-history-tab',
  ],
  computed: {
    saleId() {
      return Number(this.sale?.id || 0) || null;
    },
    isMultiCustomerSale() {
      const countCandidates = [
        this.sale?.customers_count,
        this.sale?.companies_count,
        this.sale?.clients_count,
      ];

      const hasManyCustomers = countCandidates.some((candidate) => Number(candidate || 0) > 1);
      const hasCustomersArray = Array.isArray(this.sale?.customers) && this.sale.customers.length > 1;
      const multiFlag = Number(this.sale?.is_multi_customer || this.sale?.multi_customer || 0) === 1;
      const typeLabel = String(this.sale?.sale_type || this.sale?.type || this.sale?.sale_mode || '').trim().toLowerCase();

      return hasManyCustomers || hasCustomersArray || multiFlag || typeLabel.includes('multi');
    },
  },
};
</script>

<style scoped>
.sale-detail-tab {
  border: 1px solid rgba(153, 152, 153, 0.2);
  border-radius: 10px;
  box-shadow: 0 3px 12px rgba(0, 98, 163, 0.06);
}

.sale-detail-tab__header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
}

.sale-detail-tab__header p {
  margin: 6px 0 0;
  color: #6b7280;
  font-size: 13px;
}

.sale-detail-tab__content {
  margin-top: 16px;
  border: 1px dashed rgba(148, 163, 184, 0.6);
  border-radius: 8px;
  padding: 14px;
  background: #f8fafc;
}

.sale-detail-tab__content p {
  margin: 0;
  color: #334155;
  font-size: 14px;
}

.sale-detail-tab__content small {
  display: block;
  margin-top: 6px;
  color: #64748b;
  font-size: 11px;
}
</style>
